/**
 * Pinia bulk-apply preview store (Phase 4.6 BulkApplySetModal).
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase 4.6 BulkApplySetModal (full UI).
 *
 * Wraps the Phase 4.5 preview endpoint at
 *   POST /api/decoration_sets/<id>/preview_apply/
 * and the Phase 4.7 apply endpoint (not yet shipped) at
 *   POST /api/decoration_sets/<id>/apply/
 *
 * Per-product user overrides:
 *   - remapOverrides[cpId][setPlacementId] = zoneSlug | null
 *       null encodes the user's explicit "drop this placement" choice.
 *   - conflictResolutions[cpId][existingDecorationId] = 'replace' | 'skip'
 *
 * "dirty" flips to true the first time the user overrides anything; the
 * modal uses it to gate the cancel-with-edits confirm (P4-Q15).
 *
 * apply():
 *   - On success: applyResult holds the response payload, applyError null.
 *   - On 404 (P4.7 not yet shipped on the target sandbox): applyError
 *     surfaces a "pending" message so the modal stays open and informs
 *     the user. The modal does NOT simulate state.
 *   - On other errors: applyError surfaces the message.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/services/api'
import { SUPPRESS_ERROR_TOAST } from '@/interceptors/authResponseInterceptor'
import { inPlaceApiError } from '@/utils/safeApiError'

export const useBulkApplyPreviewStore = defineStore(
    'bulkApplyPreview',
    () => {
        // ---- preview state ----
        const previewLoading = ref(false)
        const previewError = ref(null)
        const perProduct = ref([])
        const summary = ref(null)

        // ---- user-controlled state ----
        const mode = ref('merge')
        const remapOverrides = ref({})
        const conflictResolutions = ref({})
        const dirty = ref(false)

        // ---- apply state ----
        const applyLoading = ref(false)
        const applyError = ref(null)
        const applyResult = ref(null)

        async function fetchPreview({ setId, campaignProductIds, mode: m }) {
            previewLoading.value = true
            previewError.value = null
            try {
                const res = await api.post(
                    `/decoration_sets/${setId}/preview_apply/`,
                    {
                        campaign_product_ids: campaignProductIds,
                        mode: m,
                    },
                )
                const body = res?.data || {}
                perProduct.value = Array.isArray(body.per_product)
                    ? body.per_product
                    : []
                summary.value = body.summary || null
            } catch (e) {
                previewError.value = e?.message || 'preview failed'
                perProduct.value = []
                summary.value = null
            } finally {
                previewLoading.value = false
            }
        }

        function setMode(next) {
            mode.value = next
        }

        function setRemapOverride(cpId, setPlacementId, zoneSlug) {
            const cpKey = String(cpId)
            const spKey = String(setPlacementId)
            if (!remapOverrides.value[cpKey]) {
                remapOverrides.value[cpKey] = {}
            }
            remapOverrides.value[cpKey][spKey] = zoneSlug
            dirty.value = true
        }

        function setConflictResolution(cpId, existingDecorationId, choice) {
            const cpKey = String(cpId)
            const edKey = String(existingDecorationId)
            if (!conflictResolutions.value[cpKey]) {
                conflictResolutions.value[cpKey] = {}
            }
            conflictResolutions.value[cpKey][edKey] = choice
            dirty.value = true
        }

        function reset() {
            previewLoading.value = false
            previewError.value = null
            perProduct.value = []
            summary.value = null
            mode.value = 'merge'
            remapOverrides.value = {}
            conflictResolutions.value = {}
            dirty.value = false
            applyLoading.value = false
            applyError.value = null
            applyResult.value = null
        }

        async function apply({ setId, campaignProductIds }) {
            applyLoading.value = true
            applyError.value = null
            applyResult.value = null
            // Combine remap overrides + conflict resolutions into a
            // per-product resolutions map.
            const resolutions = {}
            const cpKeys = new Set([
                ...Object.keys(remapOverrides.value),
                ...Object.keys(conflictResolutions.value),
            ])
            for (const key of cpKeys) {
                resolutions[key] = {
                    remap_overrides: remapOverrides.value[key] || {},
                    conflict_resolutions:
                        conflictResolutions.value[key] || {},
                }
            }
            try {
                const res = await api.post(
                    `/decoration_sets/${setId}/apply/`,
                    {
                        campaign_product_ids: campaignProductIds,
                        mode: mode.value,
                        per_product_resolutions: resolutions,
                    },
                    // The modal shows this failure itself
                    // (bulk-apply-apply-error): no app toast on top.
                    { [SUPPRESS_ERROR_TOAST]: true },
                )
                applyResult.value = res?.data || null
            } catch (e) {
                const status = e?.response?.status
                if (status === 404) {
                    applyError.value =
                        'Apply endpoint pending (P4.7) -- preview only for now.'
                } else {
                    applyError.value = inPlaceApiError(e, 'apply failed')
                }
                applyResult.value = null
            } finally {
                applyLoading.value = false
            }
        }

        return {
            // state
            previewLoading,
            previewError,
            perProduct,
            summary,
            mode,
            remapOverrides,
            conflictResolutions,
            dirty,
            applyLoading,
            applyError,
            applyResult,
            // actions
            fetchPreview,
            setMode,
            setRemapOverride,
            setConflictResolution,
            reset,
            apply,
        }
    },
)
